import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Logger, LoggerFactory } from '../../../../../core/logger.service';
import { LanguageService } from '../../../../../core/language.service';
import { IdentifiedRateStat } from '../../../audience.model';
import { ThemeService } from 'app/shared/services/theme.service';
import { Subscription } from 'rxjs';
import { FormatNumberPipe } from 'app/shared/pipes/format-number.pipe';
import TimeUtils from 'app/shared/utils/time-utils';
import {
  applyChartFontDefaults,
  CHART_LINE_TENSION,
  CHART_TICK_FONT_SIZE,
  dotLegendLabels,
  resolveChartAxisColors,
  resolveChartPointStyle,
  withAlpha,
} from 'app/shared/chart-colors/chart-config';

declare let Chart: any;

/**
 * A chart that draws a total uses this fixed palette rather than --chartIdentifiedColor, so the
 * area stays behind the lines in every theme.
 */
const TOTAL_AREA_COLOR = '#fcd877';
const TOTAL_AREA_GRADIENT = '252, 216, 119';
const TOTAL_IDENTIFIED_COLOR = '#1b9e4b';

@Component({
  selector: 'app-audience-identified-rate-chart',
  templateUrl: './audience-identified-rate-chart.component.html',
  styleUrls: ['./audience-identified-rate-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudienceIdentifiedRateChartComponent implements OnInit, OnDestroy {
  private readonly logger: Logger;
  @Input() public height: number;
  @Input() public average: number;
  @ViewChild('chartCanvas', { static: true }) chartCanvasRef: ElementRef;
  private myChart;
  private _dataArray: Array<IdentifiedRateStat>;
  private months;
  private defaultHeight = 130;
  public isDarkThemeSub: Subscription;
  private subscriptionArray: Subscription[] = [];
  public isDarkTheme: boolean;

  @Input()
  public set dataArray(value: Array<IdentifiedRateStat>) {
    this._dataArray = value;
    if (value) {
      this.renderChart();
    } else {
      if (this.myChart !== undefined) {
        this.myChart.destroy();
      }
      this.chartCanvasRef.nativeElement.innerHTML = '';
    }
  }

  public dataSetConfigs = {
    identified: {
      labelKey: 'audience.statistics.identified-rate',
      lineColor: '#488EFF',
      backgroundColor: '#488EFF',
      gradientColor: '72, 142, 255',
    },
    average: {
      labelKey: 'audience.statistics.average',
      lineColor: '#000',
      backgroundColor: '#000',
      gradientColor: '#000',
    },
    identifiedVisitors: {
      labelKey: 'dashboard.statistics.websiteVisitors.identifiedVisitors',
      // Overridden per render; the old dashboard's legend swatch binds the same token.
      lineColor: '#ffbc07',
      backgroundColor: '#ffbc07',
      gradientColor: '255, 188, 7',
    },
    unidentifiedVisitors: {
      labelKey: 'dashboard.statistics.websiteVisitors.anonymousVisitors',
      lineColor: '#555',
      backgroundColor: '#555',
      gradientColor: '#555',
    },
    totalVisitors: {
      labelKey: 'dashboard.statistics.websiteVisitors.totalVisitors',
      lineColor: TOTAL_AREA_COLOR,
      backgroundColor: TOTAL_AREA_COLOR,
      gradientColor: TOTAL_AREA_GRADIENT,
    },
    loggedInVisitors: {
      labelKey: 'dashboard.statistics.websiteVisitors.loggedInVisitors',
      lineColor: '#516ce3',
      backgroundColor: '#516ce3',
      // Unused while the series draws unfilled, as with unidentifiedVisitors; the config shape wants it.
      gradientColor: '#516ce3',
    },
  };

  constructor(
    private myElement: ElementRef,
    loggerFactory: LoggerFactory,
    private languageService: LanguageService,
    public themeService: ThemeService,
    private formatNumberPipe: FormatNumberPipe,
  ) {
    this.logger = loggerFactory.get('AudienceAnalyticsChartComponent');
    this.months = this.languageService.translateInstantly('time.months');
  }

  ngOnInit(): void {
    this.isDarkThemeSub = this.themeService.isDarkThemeBS.subscribe((isDarkTheme) => {
      this.isDarkTheme = isDarkTheme;
      this.dataSetConfigs.unidentifiedVisitors.lineColor =
        this.dataSetConfigs.unidentifiedVisitors.backgroundColor =
        this.dataSetConfigs.unidentifiedVisitors.gradientColor =
          isDarkTheme ? 'gray' : '#555';
      this.dataSetConfigs.average.lineColor = this.dataSetConfigs.average.backgroundColor = isDarkTheme ? '#f5f5f5' : '#000';
      this.renderChart();
    });
  }

  // The canvas cannot read a CSS variable, so resolve the theme's colour here. The gradient wants a
  // bare "r, g, b" triplet, which withAlpha gives us once the "rgba(" wrapper and alpha are stripped.
  private applyIdentifiedColor(): void {
    const color = getComputedStyle(document.body).getPropertyValue('--chartIdentifiedColor').trim();
    if (!color) {
      return;
    }
    const rgba = withAlpha(color, 1);
    const triplet = rgba.startsWith('rgba(') ? rgba.slice(5, rgba.lastIndexOf(',')) : null;
    if (!triplet) {
      return;
    }
    const config = this.dataSetConfigs.identifiedVisitors;
    config.lineColor = color;
    config.backgroundColor = color;
    config.gradientColor = triplet;
  }

  private renderChart() {
    // The chart's other users never set a total, so this gate leaves their colours and fills alone.
    const hasTotal = this._dataArray?.some((x) => x.total) ?? false;
    if (hasTotal) {
      const identified = this.dataSetConfigs.identifiedVisitors;
      identified.lineColor = identified.backgroundColor = TOTAL_IDENTIFIED_COLOR;
    } else {
      this.applyIdentifiedColor();
    }
    const dataSettings = { labels: [], datasets: [] };
    const axis = resolveChartAxisColors(this.isDarkTheme);
    let options;

    // Date headers from first item (all have same dates)
    if (this._dataArray && this._dataArray.length > 0) {
      this._dataArray.forEach((item) => {
        const date = item.date;
        if (dataSettings.labels.indexOf(date) === -1) {
          dataSettings.labels.push(date);
        }
      });
    }
    const isIdentifiedPercentage = this._dataArray.some((x) => x.identifiedPercentage);
    const isIdentifiedVisitors = this._dataArray.some((x) => x.identified);

    if (isIdentifiedPercentage) {
      dataSettings.datasets.push(
        this.generateDataSet(
          this._dataArray.map((x) => x.identifiedPercentage),
          this.dataSetConfigs.identified,
          'yAxes',
          true,
          'line',
        ),
      );
    }

    // Highest order, so it draws behind every line.
    if (hasTotal) {
      dataSettings.datasets.push(
        this.generateDataSet(
          this._dataArray.map((x) => x.total),
          this.dataSetConfigs.totalVisitors,
          'yAxes',
          true,
          'line',
          false,
          3,
        ),
      );
    }

    if (isIdentifiedVisitors) {
      dataSettings.datasets.push(
        this.generateDataSet(
          this._dataArray.map((x) => x.identified),
          this.dataSetConfigs.identifiedVisitors,
          'yAxes',
          !hasTotal,
          'line',
          false,
          2,
        ),
      );
    }

    if (this._dataArray.some((x) => x.unidentified)) {
      dataSettings.datasets.push(
        this.generateDataSet(
          this._dataArray.map((x) => x.unidentified),
          this.dataSetConfigs.unidentifiedVisitors,
          'yAxes',
          false,
          'line',
          false,
          1,
        ),
      );
    }

    // Same truthiness test as the series above: an all-zero series is dropped rather than drawn flat,
    // and the other users of this chart never set the field at all.
    if (this._dataArray.some((x) => x.loggedIn)) {
      dataSettings.datasets.push(
        this.generateDataSet(
          this._dataArray.map((x) => x.loggedIn),
          this.dataSetConfigs.loggedInVisitors,
          'yAxes',
          false,
          'line',
          false,
          1,
        ),
      );
    }

    if (this.average) {
      const averageDataSet = [] as Array<number>;
      this._dataArray.forEach(() => averageDataSet.push(this.average));
      dataSettings.datasets.push(
        this.generateDataSet(averageDataSet, this.dataSetConfigs.average, 'yAxes', false, 'line', true),
      );
    }
    options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          usePointStyle: true,
          mode: 'index',
          callbacks: {
            title: (context) => {
              return TimeUtils.getFormattedDate(context[0].label);
            },
          },
        },
        legend: {
          align: 'start',
          display: dataSettings.datasets.length > 1 && !isIdentifiedVisitors && !isIdentifiedPercentage, // Hide labels if we only have one legend
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            generateLabels: (chart: any) => dotLegendLabels(chart),
          },
          // onClick: (e) => e.stopPropagation()
        },
      },
      scales: {
        x: {
          display: true,
          offset: false,
          barPercentage: 0.8,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
          ticks: {
            autoSkipPadding: 50,
            callback: (value, index, values) => {
              const date = this._dataArray.filter((item, idx) => idx === value);
              return this.formatDate(date[0].date);
            },
            color: axis.text,
            font: { size: CHART_TICK_FONT_SIZE },
          },
        },
        yAxes: {
          display: true,
          position: 'left',
          ticks: {
            min: 0,
            max: 100,
            stepSize: 10,
            maxTicksLimit: 6,
            color: axis.text,
            font: { size: CHART_TICK_FONT_SIZE },
            padding: 10,
            callback: (value) => {
              return this.formatNumberPipe.transform(value);
            },
          },
          grid: {
            display: true,
            drawTicks: false,
            color: axis.grid,
          },
          border: {
            display: false,
          },
        },
      },
    };

    // Get canvas
    const canvas = <HTMLCanvasElement>this.chartCanvasRef.nativeElement;
    if (!canvas) {
      this.logger.error('Canvas not found');
      return;
    }
    const ctx = canvas.getContext('2d');

    // Destroy old chart if it exists
    if (this.myChart != null) {
      this.myChart.destroy();
    }

    this.renderChartWhenLoaded(ctx, dataSettings, options);
  }

  private renderChartWhenLoaded(ctx: CanvasRenderingContext2D, dataSettings: any, options: any) {
    // Init component
    const legendMargin = {
      id: 'legendMargin',
      beforeInit(chart, legend, options) {
        const fitValue = chart.legend.fit;
        chart.legend.fit = function fit() {
          fitValue.bind(chart.legend)();
          return (this.height += 15);
        };
      },
    };

    applyChartFontDefaults();

    this.myChart = new Chart(ctx, {
      type: 'bar',
      data: dataSettings,
      options: options,
      plugins: [legendMargin],
    });
  }

  public setGradient(config) {
    const canvas = <HTMLCanvasElement>this.chartCanvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    const opacity = 0.33;
    const transformColor = `rgba(${config.gradientColor}, ${opacity})`;
    const endColor = `rgba(${config.gradientColor}, 0.05)`;
    const heightChart = this.height || this.defaultHeight;
    const gradient = ctx.createLinearGradient(0, 0, 0, heightChart - heightChart / 10);
    gradient.addColorStop(0, transformColor);
    gradient.addColorStop(1, endColor);
    return gradient;
  }

  private generateDataSet(
    data: Array<any>,
    config: {
      labelKey: string;
      lineColor: string;
      backgroundColor: string;
      gradientColor: string;
    },
    axisId: string,
    fill = true,
    type?: string,
    isAverageLine = false,
    order = 1,
  ) {
    const pointStyle = resolveChartPointStyle(this._dataArray.length, config.lineColor, this.isDarkTheme);
    return {
      label: this.languageService.translateInstantly(config.labelKey),
      hidden: false,
      data: data,
      borderColor: config.lineColor,
      backgroundColor: !fill ? config.backgroundColor : this.setGradient(config),
      fill: fill,
      borderWidth: isAverageLine ? 1 : 2,
      ...pointStyle,
      pointRadius: isAverageLine ? 0 : pointStyle.pointRadius,
      lineTension: isAverageLine ? 0 : CHART_LINE_TENSION,
      yAxisID: axisId,
      type: type,
      order: order,
    };
  }

  private formatDate(dateString) {
    const date = new Date(dateString);
    return date.getDate() + ' ' + this.months[date.getMonth()];
  }

  ngOnDestroy() {
    this.chartCanvasRef.nativeElement.innerHTML = '';
    if (this.myChart != null) {
      this.myChart.destroy();
    }
    this.subscriptionArray.push(this.isDarkThemeSub);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
